import React, { useState } from 'react';
import { X, AlertCircle, CheckCircle, Loader2, TrendingUp, TrendingDown } from 'lucide-react';
import { Position } from '../../types/position';
import { formatDateWithYear } from '../../utils/format';
import Modal from '../ui/Modal';

interface ClosePositionModalProps {
  position: Position;
  onClose: () => void;
  onConfirm: (positionId: string, exitPremium: number, exitStockPrice?: number) => Promise<void>;
}

const ClosePositionModal: React.FC<ClosePositionModalProps> = ({
  position,
  onClose,
  onConfirm
}) => {
  const isShares = position.strategy === 'SHARES';

  // Default exit premium to current_premium if available, else entry_premium
  const [exitPremium, setExitPremium] = useState(
    isShares
      ? (position.current_stock_price ?? position.stock_entry_price ?? position.price_at_entry ?? 0)
      : (position.current_premium ?? position.entry_premium * 0.5)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Calculate P&L preview based on entered exit premium
  const entryValue = isShares
    ? (position.stock_entry_price ?? position.price_at_entry ?? 0) * 100 * position.contracts
    : position.entry_premium * 100 * position.contracts;
  const exitValue = exitPremium * 100 * position.contracts;
  // Shares profit when stock price rises; short options profit when option premium falls
  const pnl = isShares ? (exitValue - entryValue) : (entryValue - exitValue);
  const pnlPercent = entryValue > 0 ? (pnl / entryValue) * 100 : 0;
  const isProfit = pnl > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const stockExitPrice = isShares ? exitPremium : (position.current_stock_price ?? undefined);
      await onConfirm(position.id, exitPremium, stockExitPrice);
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close position');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div>
            <h2 className="text-lg font-bold text-white">Close Position</h2>
            <p className="text-sm text-slate-400">{position.symbol} • {position.strategy}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Success State */}
        {success ? (
          <div className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Position Closed!</h3>
            <p className="text-slate-400">Your trade has been recorded.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Position Details */}
            <div className="p-4 space-y-4">
              {/* Position Summary */}
              <div className="bg-slate-900/50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Strike Price</span>
                  <span className="text-white font-medium">${position.strike}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Expiration</span>
                  <span className="text-white font-medium">{formatDateWithYear(position.expiration)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Contracts</span>
                  <span className="text-white font-medium">{position.contracts}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Entry Premium</span>
                  <span className="text-cyan-400 font-medium">${position.entry_premium.toFixed(2)}</span>
                </div>
              </div>

              {/* Exit Premium Input */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  {isShares ? 'Your Exit Stock Price' : 'Your Exit Premium (Buy to Close)'}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={exitPremium}
                    onChange={(e) => setExitPremium(parseFloat(e.target.value) || 0)}
                    className="w-full pl-7 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {isShares ? 'Enter the stock sale price' : 'Enter the price you bought to close at'}
                </p>
              </div>

              {/* P&L Preview */}
              <div className={`rounded-lg p-3 space-y-2 border ${isProfit ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {isProfit ? (
                    <TrendingUp className="w-4 h-4 text-green-400" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-red-400" />
                  )}
                  <span className={`text-sm font-medium ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
                    P&L Preview
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Entry Value</span>
                  <span className="text-white font-medium">${entryValue.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Exit Value</span>
                  <span className="text-white font-medium">${exitValue.toFixed(2)}</span>
                </div>
                <div className="border-t border-slate-600 pt-2 mt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Est. P&L <span className="text-slate-500 text-xs">(preview)</span></span>
                    <span className={`font-bold ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
                      {isProfit ? '+' : ''}{pnl >= 0 ? '$' : '-$'}{Math.abs(pnl).toFixed(2)} ({pnlPercent >= 0 ? '+' : ''}{pnlPercent.toFixed(1)}% {isShares ? 'ROI' : 'of premium'})
                    </span>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <span className="text-sm text-red-400">{error}</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 p-4 border-t border-slate-700">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2.5 px-4 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || exitPremium < 0}
                className="flex-1 py-2.5 px-4 rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Closing...
                  </>
                ) : (
                  'Close Position'
                )}
              </button>
            </div>
          </form>
        )}
    </Modal>
  );
};

export default ClosePositionModal;
